import { useMutation } from '@tanstack/react-query';
import { AnalysisParams, MentalHealthReport } from '@/util/bcg/types';
import { analyzeBCGData } from '@/util/bcg';
import { mentalHealthStore } from '@/store/MentalHealthStore';

export interface BCGUploadRequest {
  files: File[];
  params: AnalysisParams;
  userId?: number;
  userName?: string;
}

export interface BCGUploadResponse {
  success: boolean;
  reports: Array<{
    filename: string;
    report: MentalHealthReport;
    error?: string;
  }>;
}

/**
 * CSV 파일을 파싱하여 객체 배열로 변환 (실제 BCG 데이터 형식에 맞게)
 */
function parseCSV(csvText: string): any[] {
  const lines = csvText.split('\n').filter(line => line.trim());
  if (lines.length < 2) return [];
  
  const headers = lines[0].split(',').map(h => h.trim());
  const rows = [];
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    // bcgMerge 필드가 JSON이므로 특별한 파싱이 필요
    const parts: string[] = [];
    let inJson = false;
    let currentPart = '';
    let braceCount = 0;
    
    for (let j = 0; j < line.length; j++) {
      const char = line[j];
      
      if (char === '{') {
        braceCount++;
        inJson = true;
      } else if (char === '}') {
        braceCount--;
        if (braceCount === 0) {
          inJson = false;
        }
      }
      
      if (char === ',' && !inJson) {
        parts.push(currentPart.trim());
        currentPart = '';
      } else {
        currentPart += char;
      }
    }
    
    if (currentPart) {
      parts.push(currentPart.trim());
    }
    
    if (parts.length >= headers.length) {
            const row: any = {};
            headers.forEach((header, index) => {
              if (index < parts.length) {
                let value = parts[index];
                // bcgMerge 필드의 경우 이중 이스케이프된 JSON 문자열 수정
                if (header === 'bcgMerge' && typeof value === 'string') {
                  // 앞뒤 따옴표 제거 및 이중 따옴표를 단일 따옴표로 변환
                  value = value.replace(/^"/, '').replace(/"$/, '').replace(/""/g, '"');
                }
                row[header] = value;
              }
            });
            rows.push(row);
    }
  }
  
  return rows;
}

/**
 * 파일을 읽어서 텍스트로 변환
 */
function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('파일을 텍스트로 읽을 수 없습니다.'));
      }
    };
    
    reader.onerror = (e) => {
      console.error('FileReader 오류:', e);
      reject(new Error(`파일 읽기 실패: ${file.name}`));
    };
    
    reader.onabort = () => {
      reject(new Error('파일 읽기가 중단되었습니다.'));
    };
    
    // UTF-8로 명시적으로 읽기
    try {
      reader.readAsText(file, 'UTF-8');
    } catch (error) {
      reject(new Error(`파일 읽기 시작 실패: ${error}`));
    }
  });
}

/**
 * BCG 데이터 클라이언트 사이드 분석
 */
async function uploadBCGData(request: BCGUploadRequest): Promise<BCGUploadResponse> {
  const reports: BCGUploadResponse['reports'] = [];
  
  for (const file of request.files) {
    try {
      // console.log(`파일 처리 시작: ${file.name}, 크기: ${file.size} bytes, 타입: ${file.type}`);
      
      // 파일 크기 체크 (100MB 제한)
      if (file.size > 100 * 1024 * 1024) {
        reports.push({
          filename: file.name,
          report: {} as MentalHealthReport,
          error: '파일 크기가 너무 큽니다 (100MB 제한).'
        });
        continue;
      }
      
      // 파일 읽기
      const csvText = await readFileAsText(file);
      // console.log(`파일 읽기 완료: ${file.name}, 텍스트 길이: ${csvText.length}`);
      
      if (!csvText || csvText.trim().length === 0) {
        reports.push({
          filename: file.name,
          report: {} as MentalHealthReport,
          error: '파일이 비어있습니다.'
        });
        continue;
      }
      
      const csvData = parseCSV(csvText);
      // console.log(`CSV 파싱 완료: ${file.name}, 행 수: ${csvData.length}`);
      
      if (csvData.length === 0) {
        reports.push({
          filename: file.name,
          report: {} as MentalHealthReport,
          error: 'CSV 파일이 비어있거나 형식이 올바르지 않습니다.'
        });
        continue;
      }
      
      // 파싱된 데이터 구조 확인
      // console.log(`첫 번째 행 샘플:`, csvData[0]);
      // console.log(`헤더 확인:`, Object.keys(csvData[0]));
      
      // bcgMerge 데이터 확인
      if (csvData[0]?.bcgMerge) {
        try {
          JSON.parse(csvData[0].bcgMerge);
          // const bcgSample = JSON.parse(csvData[0].bcgMerge);
          // console.log(`BCG 데이터 샘플:`, bcgSample);
          // console.log(`BCG 데이터 길이:`, bcgSample?.data?.length);
        } catch (e) {
          console.error(`BCG JSON 파싱 오류:`, e);
        }
      }
      
      // BCG 분석 수행
      // console.log(`분석 시작: ${file.name}`);
      const report = analyzeBCGData(csvData, request.params);
      
      if (report) {
        // console.log(`분석 완료: ${file.name}, 점수: ${report.mindScore.score}`);
        
        // 분석 결과를 저장소에 저장 (userId, userName 포함)
        mentalHealthStore.saveReport(
          file.name, 
          report, 
          request.userId, 
          request.userName
        );
        // const storedReport = mentalHealthStore.saveReport(
        //   file.name, 
        //   report, 
        //   request.userId, 
        //   request.userName
        // );
        // console.log(`저장 완료: ID ${storedReport.id}, User: ${storedReport.userName} (${storedReport.userId})`);
        
        reports.push({
          filename: file.name,
          report,
        });
      } else {
        reports.push({
          filename: file.name,
          report: {} as MentalHealthReport,
          error: '분석할 수 있는 데이터가 부족합니다.'
        });
      }
    } catch (error) {
      console.error(`파일 처리 오류: ${file.name}`, error);
      reports.push({
        filename: file.name,
        report: {} as MentalHealthReport,
        error: `파일 처리 중 오류: ${error instanceof Error ? error.message : '알 수 없는 오류'}`
      });
    }
  }
  
  return {
    success: true,
    reports
  };
}

/**
 * BCG 데이터 업로드 및 분석 Hook
 */
export function useUploadBCGData() {
  return useMutation({
    mutationFn: uploadBCGData,
    onError: (error) => {
      console.error('BCG 데이터 업로드 실패:', error);
    },
  });
}
